import { themeJsonUrl } from '../data/constants'
import { toRgbTriple, isCssColorFunction } from './colorToRgb'
import { injectStudioFontLinks, removeStudioFontLinks } from './studioFonts'
import { SP_THEME_JSON_SESSION_KEY } from './themeGate'
import {
  FONT_FAMILY_CSS_VARS,
  FONT_LINE_HEIGHT_DISPLAY,
  FONT_SIZE_CSS_VARS,
  FONT_TRACKING_DISPLAY,
  FONT_WEIGHT_CSS_VARS,
  LINE_CSS_VAR,
  PALETTE_CSS_VARS,
  PaletteKey,
  RADIUS_CSS_VARS,
  CSS_SLOT_MAP,
  RADIUS_SLOT_MAP,
  THEME_SLOT_MAP,
} from './themeSlotMap'

const DARKEN_COLOR_WEIGHT = 0.82

type StudioThemeColors = {
  accent?: string
  accent2?: string
  accentInk?: string
  bg?: string
  surface?: string
  surfaceAlt?: string
  text?: string
  textMuted?: string
  line?: string
  chip?: string
}

type StudioThemeRadius = {
  sm?: string
  md?: string
  lg?: string
  pill?: string
}

export type StudioThemeTypography = {
  display?: string
  body?: string
  sizeHeader?: string
  sizeBody?: string
  displayWeight?: string | number
  bodyWeight?: string | number
  displayTracking?: string
  displayLineHeight?: string
}

type StudioThemeFile = {
  _type?: string
  theme?: {
    colors?: StudioThemeColors
    radius?: StudioThemeRadius
    typography?: StudioThemeTypography
  }
}

const TRIPLE_KEYS: Exclude<PaletteKey, 'line'>[] = [
  'accent',
  'accent2',
  'accentInk',
  'bg',
  'surface',
  'surfaceAlt',
  'text',
  'textMuted',
]

function asCssString(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null) return undefined
  const s = String(value).trim()
  return s || undefined
}

/** Palette CSS updates from Studio typography. Empty object if nothing to write. */
export function typographyCssUpdates(typo: StudioThemeTypography): Record<string, string> {
  const out: Record<string, string> = {}
  const body = asCssString(typo.body)
  const display = asCssString(typo.display)
  const sizeBody = asCssString(typo.sizeBody)
  const sizeHeader = asCssString(typo.sizeHeader)
  const bodyWeight = asCssString(typo.bodyWeight)
  const displayWeight = asCssString(typo.displayWeight)
  const tracking = asCssString(typo.displayTracking)
  const lineHeight = asCssString(typo.displayLineHeight)
  if (body) out[FONT_FAMILY_CSS_VARS.body] = body
  if (display) out[FONT_FAMILY_CSS_VARS.display] = display
  if (sizeBody) out[FONT_SIZE_CSS_VARS.body] = sizeBody
  if (sizeHeader) out[FONT_SIZE_CSS_VARS.header] = sizeHeader
  if (bodyWeight) out[FONT_WEIGHT_CSS_VARS.body] = bodyWeight
  if (displayWeight) out[FONT_WEIGHT_CSS_VARS.display] = displayWeight
  if (tracking) out[FONT_TRACKING_DISPLAY] = tracking
  if (lineHeight) out[FONT_LINE_HEIGHT_DISPLAY] = lineHeight
  return out
}

export function clearInlineTheme(root: HTMLElement = document.documentElement): void {
  TRIPLE_KEYS.forEach((key) => root.style.removeProperty(PALETTE_CSS_VARS[key]))
  root.style.removeProperty(LINE_CSS_VAR)
  Object.values(RADIUS_CSS_VARS).forEach((cssVar) => root.style.removeProperty(cssVar))
  Object.values(FONT_FAMILY_CSS_VARS).forEach((cssVar) => root.style.removeProperty(cssVar))
  Object.values(FONT_SIZE_CSS_VARS).forEach((cssVar) => root.style.removeProperty(cssVar))
  Object.values(FONT_WEIGHT_CSS_VARS).forEach((cssVar) => root.style.removeProperty(cssVar))
  root.style.removeProperty(FONT_TRACKING_DISPLAY)
  root.style.removeProperty(FONT_LINE_HEIGHT_DISPLAY)
  Object.keys(THEME_SLOT_MAP).forEach((slot) => root.style.removeProperty(`--slot-${slot}`))
  Object.keys(RADIUS_SLOT_MAP).forEach((slot) => root.style.removeProperty(`--slot-${slot}`))
  Object.keys(CSS_SLOT_MAP).forEach((slot) => root.style.removeProperty(`--slot-${slot}`))
  removeStudioFontLinks()
}

function writePalette(root: HTMLElement, colors: StudioThemeColors, radius?: StudioThemeRadius): void {
  TRIPLE_KEYS.forEach((key) => {
    const raw = colors[key]
    if (!raw) return
    const triple = toRgbTriple(raw)
    if (triple) root.style.setProperty(PALETTE_CSS_VARS[key], triple)
  })
  if (colors.line) {
    if (isCssColorFunction(colors.line)) {
      root.style.setProperty(LINE_CSS_VAR, colors.line.trim())
    } else {
      const triple = toRgbTriple(colors.line)
      if (triple) root.style.setProperty(LINE_CSS_VAR, `rgb(${triple})`)
    }
  }
  if (!radius) return
  ;(Object.keys(RADIUS_CSS_VARS) as Array<keyof typeof RADIUS_CSS_VARS>).forEach((key) => {
    const raw = radius[key]
    if (raw) root.style.setProperty(RADIUS_CSS_VARS[key], raw)
  })
}

function applyRadiusSlots(root: HTMLElement): void {
  Object.entries(RADIUS_SLOT_MAP).forEach(([name, entry]) => {
    const fromPalette = root.style.getPropertyValue(RADIUS_CSS_VARS[entry.custom]).trim()
    if (fromPalette) root.style.setProperty(`--slot-${name}`, fromPalette)
  })
}

function applyCssSlots(root: HTMLElement): void {
  Object.entries(CSS_SLOT_MAP).forEach(([name, entry]) => {
    root.style.setProperty(`--slot-${name}`, entry.custom)
  })
}

function writeTypography(root: HTMLElement, typo?: StudioThemeTypography): void {
  if (!typo) return
  const updates = typographyCssUpdates(typo)
  Object.entries(updates).forEach(([cssVar, value]) => root.style.setProperty(cssVar, value))
  injectStudioFontLinks([typo.body ?? '', typo.display ?? ''].filter(Boolean))
}

type ThemeJsonSession = {
  merchantId: string
  file: StudioThemeFile | null
}

function isUsableThemeFile(json: StudioThemeFile | null | undefined): json is StudioThemeFile {
  return json?._type === 'qr-theme-studio.theme' && !!json.theme?.colors
}

function readThemeJsonSession(): ThemeJsonSession | null {
  try {
    const raw = sessionStorage.getItem(SP_THEME_JSON_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ThemeJsonSession
    if (!parsed?.merchantId) return null
    if (!('file' in parsed)) return null
    return parsed
  } catch {
    return null
  }
}

function writeThemeJsonSession(merchantId: string, file: StudioThemeFile | null): void {
  try {
    sessionStorage.setItem(SP_THEME_JSON_SESSION_KEY, JSON.stringify({ merchantId, file }))
  } catch {
    /* quota / private mode */
  }
}

function paintStudioTheme(root: HTMLElement, json: StudioThemeFile): void {
  const colors = json.theme?.colors
  if (!colors) return
  writePalette(root, colors, json.theme?.radius)
  writeTypography(root, json.theme?.typography)
  applySlots(root)
  applyRadiusSlots(root)
  applyCssSlots(root)
}

function applySlots(root: HTMLElement): void {
  Object.entries(THEME_SLOT_MAP).forEach(([name, entry]) => {
    const cssVar = `--slot-${name}`
    if (entry.custom === 'line') {
      const inline = root.style.getPropertyValue(LINE_CSS_VAR).trim()
      if (inline) root.style.setProperty(cssVar, inline)
      return
    }
    const paletteVar = PALETTE_CSS_VARS[entry.custom]
    const fromPalette = root.style.getPropertyValue(paletteVar).trim()
    if (!fromPalette) return
    // Keep 82% of the colour, 18% black — SmartWeb's shade-900 weight, so both apps match.
    const triple = entry.darken
      ? fromPalette.split(',').map((c) => Math.round(Number(c) * DARKEN_COLOR_WEIGHT)).join(', ')
      : fromPalette
    if (entry.kind === 'css') {
      root.style.setProperty(cssVar, `rgb(${triple})`)
      return
    }
    root.style.setProperty(cssVar, triple)
  })
}

export async function applyStudioTheme(
  pathMerchantId: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const root = document.documentElement
  const cached = readThemeJsonSession()
  if (cached?.merchantId === pathMerchantId) {
    if (cached.file === null) {
      clearInlineTheme(root)
      return false
    }
    if (isUsableThemeFile(cached.file)) {
      paintStudioTheme(root, cached.file)
      return true
    }
  }

  try {
    const res = await fetch(themeJsonUrl(pathMerchantId), { signal, cache: 'no-store' })
    if (!res.ok) {
      console.warn(`[theme] theme.json ${res.status} for ${pathMerchantId}; keeping production fallbacks`)
      writeThemeJsonSession(pathMerchantId, null)
      clearInlineTheme(root)
      return false
    }
    const json = (await res.json()) as StudioThemeFile
    if (!isUsableThemeFile(json)) {
      console.warn('[theme] unexpected theme.json shape; keeping production fallbacks')
      writeThemeJsonSession(pathMerchantId, null)
      clearInlineTheme(root)
      return false
    }
    paintStudioTheme(root, json)
    writeThemeJsonSession(pathMerchantId, json)
    return true
  } catch (err) {
    if (signal?.aborted) return false
    console.warn('[theme] failed to fetch theme.json; keeping production fallbacks', err)
    clearInlineTheme(root)
    return false
  }
}
